/**
 * WidgetHost — dispatches a DashboardWidget to its renderer. BUILTIN widgets
 * fan out to builtin/<X>Host.tsx by builtinRef; the 5 recharts-backed hosts
 * (STATUS_DONUT/HEALTH_DONUT/THROUGHPUT/RAID_BAR/PER_RESOURCE_CHART) are
 * React.lazy so a KPI/table-only dashboard never pays the recharts bundle
 * cost (design brief PERF — verified in T9's build-output inspection). The
 * other 4 addable kinds (STAT_KPIS/CAPACITY_PANEL/ACCOUNTS_TABLE/
 * PROJECT_RAG_TABLE) have no recharts dependency and load eagerly.
 * METRIC widgets (M3) lazy-load MetricWidgetHost — its own React.lazy chunk,
 * so a KPI/TABLE-only dashboard still never pays the recharts cost. TEXT/
 * HEADER/LINKS remain later-milestone kinds and fall through to the inert
 * placeholder.
 */
import { Suspense, lazy } from 'react';
import type { DashboardWidgetResponse, BuiltinWidget } from '@/types/domain';
import { WidgetSkeleton } from './WidgetSkeleton';
import { ROW_HEIGHT } from './gridConstants';
import { StatKpisHost } from './builtin/StatKpisHost';
import { CapacityHost } from './builtin/CapacityHost';
import { AccountsTableHost } from './builtin/AccountsTableHost';
import { ProjectRagTableHost } from './builtin/ProjectRagTableHost';
import { UnavailableBuiltinHost } from './builtin/UnavailableBuiltinHost';

const StatusDonutHostLazy = lazy(() =>
  import('./builtin/StatusDonutHost').then((m) => ({ default: m.StatusDonutHost })),
);
const HealthDonutHostLazy = lazy(() =>
  import('./builtin/HealthDonutHost').then((m) => ({ default: m.HealthDonutHost })),
);
const ThroughputHostLazy = lazy(() =>
  import('./builtin/ThroughputHost').then((m) => ({ default: m.ThroughputHost })),
);
const RaidBarHostLazy = lazy(() =>
  import('./builtin/RaidBarHost').then((m) => ({ default: m.RaidBarHost })),
);
const PerResourceHostLazy = lazy(() =>
  import('./builtin/PerResourceHost').then((m) => ({ default: m.PerResourceHost })),
);
const MetricWidgetHostLazy = lazy(() =>
  import('./metric/MetricWidgetHost').then((m) => ({ default: m.MetricWidgetHost })),
);

const BUILTIN_TITLES: Record<BuiltinWidget, string> = {
  STAT_KPIS: 'Key metrics',
  STATUS_DONUT: 'Work items by status',
  HEALTH_DONUT: 'Project health',
  THROUGHPUT: 'Weekly throughput',
  // Canonical panel heading — CapacityHost threads this into CapacityPanel's
  // own CardHeader, so the null-title fallback must be the name the panel has
  // always displayed, not the palette's short "Capacity" label.
  CAPACITY_PANEL: 'Resource capacity',
  RAID_BAR: 'Work items with open RAIDs',
  PER_RESOURCE_CHART: 'Work items per resource',
  ACCOUNTS_TABLE: 'Accounts',
  PROJECT_RAG_TABLE: 'All projects',
  UTILIZATION_HEATMAP: 'Utilisation heatmap',
  BOTTLENECKS: 'Resource bottlenecks',
  WORKLOAD_STRIP: 'Workload',
};

export interface WidgetHostProps {
  widget: DashboardWidgetResponse;
  accountId: string | null;
  /** Threaded to the two AUTO_HEIGHT_BUILTINS hosts only (gridConstants.ts); every other builtin ignores it. */
  onRowCount?: (rowCount: number) => void;
}

function renderBuiltin(
  builtinRef: BuiltinWidget,
  title: string,
  minHeight: number,
  accountId: string | null,
  onRowCount: ((rowCount: number) => void) | undefined,
) {
  switch (builtinRef) {
    case 'STAT_KPIS':
      return <StatKpisHost title={title} minHeight={minHeight} accountId={accountId} />;
    case 'CAPACITY_PANEL':
      return <CapacityHost title={title} minHeight={minHeight} accountId={accountId} />;
    case 'ACCOUNTS_TABLE':
      return <AccountsTableHost title={title} minHeight={minHeight} accountId={accountId} onRowCount={onRowCount} />;
    case 'PROJECT_RAG_TABLE':
      return <ProjectRagTableHost title={title} minHeight={minHeight} accountId={accountId} onRowCount={onRowCount} />;
    case 'STATUS_DONUT':
      return <StatusDonutHostLazy title={title} minHeight={minHeight} accountId={accountId} />;
    case 'HEALTH_DONUT':
      return <HealthDonutHostLazy title={title} minHeight={minHeight} accountId={accountId} />;
    case 'THROUGHPUT':
      return <ThroughputHostLazy title={title} minHeight={minHeight} accountId={accountId} />;
    case 'RAID_BAR':
      return <RaidBarHostLazy title={title} minHeight={minHeight} accountId={accountId} />;
    case 'PER_RESOURCE_CHART':
      return <PerResourceHostLazy title={title} minHeight={minHeight} accountId={accountId} />;
    case 'UTILIZATION_HEATMAP':
    case 'BOTTLENECKS':
    case 'WORKLOAD_STRIP':
      return <UnavailableBuiltinHost title={title} minHeight={minHeight} accountId={accountId} />;
    default: {
      // Exhaustiveness guard: if BuiltinWidget ever grows a 13th value
      // without a case above, `builtinRef` narrows to a non-never type here
      // and this line fails to compile — a stronger, tsconfig-independent
      // guarantee than relying on noImplicitReturns (not enabled in this
      // project's tsconfig.json).
      const exhaustive: never = builtinRef;
      throw new Error(`Unhandled BuiltinWidget: ${String(exhaustive)}`);
    }
  }
}

export function WidgetHost({ widget, accountId, onRowCount }: WidgetHostProps) {
  const minHeight = widget.gridH * ROW_HEIGHT;
  const title = widget.title ?? (widget.builtinRef ? BUILTIN_TITLES[widget.builtinRef] : 'Widget');

  if (widget.kind === 'BUILTIN' && widget.builtinRef) {
    return (
      <Suspense fallback={<WidgetSkeleton />}>
        {renderBuiltin(widget.builtinRef, title, minHeight, accountId, onRowCount)}
      </Suspense>
    );
  }

  if (widget.kind === 'METRIC' && widget.metricDefinitionId && widget.viz) {
    return (
      <Suspense fallback={<WidgetSkeleton />}>
        <MetricWidgetHostLazy widget={widget} accountId={accountId} title={title} minHeight={minHeight} />
      </Suspense>
    );
  }

  // TEXT/HEADER/LINKS (and any incomplete METRIC widget) — not yet renderable.
  return (
    <div
      style={{
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'var(--text-low)',
        fontFamily: 'var(--font-sans)',
        fontSize: 13,
      }}
    >
      {title} — not supported in this view yet
    </div>
  );
}
